import type { GeometryElement } from 'jsxgraph';
import type { PointCoordsInput } from '../point/point.model';

/**
 * Check if input is an existing JSXGraph element.
 */
export function isElement(input: PointCoordsInput | GeometryElement | string): input is GeometryElement {
  return typeof input === 'object' && !Array.isArray(input) && 'elType' in input;
}

/**
 * Widen the discriminated `PointCoordsInput` union to a single mixed-tuple type.
 *
 * At runtime this is an identity function — its purpose is to collapse the three
 * union members (`PointCoords | PointCoordsFn | PointCoordsMixed`) into the
 * uniform `[number | (() => number), number | (() => number)]` that JSXGraph
 * `board.create` accepts, so callers don't need type assertions.
 */
export function normalizeCoords(coords: PointCoordsInput): [number | (() => number), number | (() => number)] {
  return coords;
}

/**
 * Compute the bisector point of an angle defined by three points.
 *
 * @param a - First ray endpoint (as {X, Y} getters)
 * @param vertex - The vertex of the angle
 * @param b - Second ray endpoint (as {X, Y} getters)
 * @param radius - Distance from vertex to the bisector point
 * @returns [x, y] coordinates of the bisector point
 */
export function getBisectorPoint(
  a: { X: () => number; Y: () => number },
  vertex: { X: () => number; Y: () => number },
  b: { X: () => number; Y: () => number },
  radius: number,
): [number, number] {
  const dx1 = a.X() - vertex.X();
  const dy1 = a.Y() - vertex.Y();
  const dx2 = b.X() - vertex.X();
  const dy2 = b.Y() - vertex.Y();

  const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
  const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

  const nx1 = dx1 / len1;
  const ny1 = dy1 / len1;
  const nx2 = dx2 / len2;
  const ny2 = dy2 / len2;

  const bx = nx1 + nx2;
  const by = ny1 + ny2;
  const blen = Math.sqrt(bx * bx + by * by);

  return [vertex.X() + (bx / blen) * radius, vertex.Y() + (by / blen) * radius];
}
